import React, { useEffect, useState } from 'react';
import { Typography, Grid, Button, Theme, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import CircularProgress from '@mui/material/CircularProgress';
import LocalVideoPreview from './LocalVideoPreview/LocalVideoPreview';
import SettingsMenu from './SettingsMenu/SettingsMenu';
import { Steps } from '../PreJoinScreens';
import ToggleAudioButton from '../../Buttons/ToggleAudioButton/ToggleAudioButton';
import ToggleVideoButton from '../../Buttons/ToggleVideoButton/ToggleVideoButton';
import { useAppState } from '../../../state';
import useChatContext from '../../../hooks/useChatContext/useChatContext';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';
import { useLocation } from 'react-router-dom';

const useStyles = makeStyles()((theme: Theme) => ({
  gutterBottom: {
    marginBottom: '1em',
  },
  marginTop: {
    marginTop: '1em',
  },
  deviceButton: {
    width: '100%',
    border: '2px solid #aaa',
    margin: '1em 0',
  },
  localPreviewContainer: {
    paddingRight: '2em',
    [theme.breakpoints.down('sm')]: {
      padding: '0 2.5em',
    },
  },
  joinButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      width: '100%',
      '& button': {
        margin: '0.5em 0',
      },
    },
  },
  mobileButtonBar: {
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      justifyContent: 'space-between',
      margin: '1.5em 0 1em',
    },
  },
  mobileButton: {
    padding: '0.8em 0',
    margin: 0,
  },
}));

interface DeviceSelectionScreenProps {
  name: string;
  roomName: string;
  setStep: (step: Steps) => void;
}

export default function DeviceSelectionScreen({ name, roomName, setStep }: DeviceSelectionScreenProps) {
  const { classes } = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  const slug = location?.search.split('?q=').pop();
  let url: string | undefined = '';

  if (slug) {
    url = slug.length > 30 ? window.atob(slug) : '';
  }
  const useQuery = () => new URLSearchParams(url);
  const query = useQuery();
  const type = query.get('type') === undefined ? '' : query.get('type');

  const { getToken, isFetching } = useAppState();
  const { connect: chatConnect } = useChatContext();
  const { connect: videoConnect, isAcquiringLocalTracks, isConnecting } = useVideoContext();
  const disableButtons = isFetching || isAcquiringLocalTracks || isConnecting;
  const [error, setError] = useState('');
  const handleJoin = () => {
    getToken(name, roomName).then(({ token, error }) => {
      if (error || !token) {
        if (error) setError(error);
        return;
      }
      videoConnect(token);
      process.env.REACT_APP_DISABLE_TWILIO_CONVERSATIONS !== 'true' && chatConnect(token);
    });
  };

  useEffect(() => {
    getToken(name, roomName).then(({ error }) => {
      if (error) {
        setError(error);
      }
    });
  }, []);

  if (isFetching || isConnecting) {
    return (
      <Grid
        container
        style={{ height: '100%' }}
        sx={{ flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}
      >
        <div>
          <CircularProgress variant="indeterminate" />
        </div>
        <div>
          <Typography variant="body2" style={{ fontWeight: 'bold', fontSize: '16px' }}>
            Joining Meeting
          </Typography>
        </div>
      </Grid>
    );
  }

  const CancelClick = () => {
    if (type === 'physician') {
      window.location.href = `${process.env.REACT_APP_PHYSICIAN_URL}/calendar`;
    } else if (type === 'hospital') {
      window.location.href = `${process.env.REACT_APP_HOSPITAL_URL}/calendar`;
    } else {
      window.location.href = '/';
    }
  };

  return (
    <>
      {error ? (
        <>
          <div style={{ color: 'red', fontWeight: 'bold' }}>{error}</div>
        </>
      ) : (
        <>
          <Typography variant="h5" className={classes.gutterBottom}>
            Join {roomName}
          </Typography>

          <Grid container sx={{ justifyContent: 'center' }}>
            <Grid size={{ md: 7, sm: 12, xs: 12 }}>
              <div className={classes.localPreviewContainer}>
                <LocalVideoPreview identity={name} />
              </div>
              <div className={classes.mobileButtonBar}>
                {isMobile && (
                  <>
                    <ToggleAudioButton className={classes.mobileButton} disabled={disableButtons} />
                    <ToggleVideoButton className={classes.mobileButton} disabled={disableButtons} />
                  </>
                )}
                <SettingsMenu mobileButtonClass={classes.mobileButton} />
              </div>
            </Grid>
            <Grid size={{ md: 5, sm: 12, xs: 12 }}>
              <Grid
                container
                style={{ height: '100%' }}
                sx={{ flexDirection: 'column', justifyContent: 'space-between' }}
              >
                <div>
                  {!isMobile && (
                    <>
                      <ToggleAudioButton className={classes.deviceButton} disabled={disableButtons} />
                      <ToggleVideoButton className={classes.deviceButton} disabled={disableButtons} />
                    </>
                  )}
                </div>
                <div className={classes.joinButtons}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => CancelClick()}
                    style={{ color: '#ff9327', border: `2px solid #ff9327` }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    data-cy-join-now
                    onClick={handleJoin}
                    disabled={disableButtons}
                    style={{ backgroundColor: '#ff9327', color: 'white' }}
                  >
                    Join Now
                  </Button>
                </div>
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
}
